import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { usePageSEO, SITE_DOMAIN } from '@/hooks/usePageSEO';

export default function ArticlePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  usePageSEO({
    title: 'What Is A Sugargoo Spreadsheet? Complete Guide 2026 | SugargooSpreadsheet.run',
    description: 'Learn everything about Sugargoo Spreadsheets — what they are, how they work, top categories, safety tips, comparison tables, and why thousands of shoppers rely on them daily. Complete 2026 guide.',
    keywords: 'Sugargoo Spreadsheet guide, what is Sugargoo Spreadsheet, Sugargoo spreadsheet tutorial, Sugargoo beginner guide, Sugargoo QC guide, Sugargoo shopping',
    canonicalUrl: `${SITE_DOMAIN}/articles/what-is-sugargoo-spreadsheet`,
    ogType: 'article',
    lastModified: '2026-06-18',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_DOMAIN },
          { '@type': 'ListItem', position: 2, name: 'What Is A Sugargoo Spreadsheet? Complete Guide 2026', item: `${SITE_DOMAIN}/articles/what-is-sugargoo-spreadsheet` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'What Is A Sugargoo Spreadsheet? Complete Guide 2026',
        description: 'Learn everything about Sugargoo Spreadsheets — what they are, how they work, and why thousands of shoppers rely on them daily.',
        author: { '@type': 'Organization', name: 'SugargooSpreadsheet.run Editorial Team' },
        datePublished: '2026-01-15',
        dateModified: '2026-06-18',
        publisher: { '@type': 'Organization', name: 'SugargooSpreadsheet.run' },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_DOMAIN}/articles/what-is-sugargoo-spreadsheet` },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          { '@type': 'Question', name: 'Is every link in a Sugargoo Spreadsheet guaranteed to work?', acceptedAnswer: { '@type': 'Answer', text: 'No. Spreadsheet links are community-curated and can expire at any time. Chinese marketplace listings are frequently removed, renamed, or relisted by sellers. Our team updates the directory weekly, but individual links may go dead between refreshes.' } },
          { '@type': 'Question', name: 'Do I need a Sugargoo account to use these spreadsheets?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. An active Sugargoo account is required to paste product links, view domestic pricing, request QC photos, and arrange international shipping.' } },
          { '@type': 'Question', name: 'What does QC mean and why is it important?', acceptedAnswer: { '@type': 'Answer', text: 'QC stands for Quality Control. Once Sugargoo receives your item at their warehouse, you can request high-resolution photos that show the actual product before it ships internationally.' } },
          { '@type': 'Question', name: 'Are the products in these spreadsheets authentic?', acceptedAnswer: { '@type': 'Answer', text: 'Most community Sugargoo Spreadsheets focus on replica, budget-friendly, or unbranded alternatives rather than authentic luxury goods. Always read item descriptions carefully and review QC photos.' } },
          { '@type': 'Question', name: 'How do I report a broken link or suggest a new find?', acceptedAnswer: { '@type': 'Answer', text: 'Return to our homepage and use the resources listed in the footer. We welcome community contributions.' } },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        <article>
          <header className="pt-24 md:pt-32 pb-12 md:pb-16 px-4 md:px-8 bg-accent-50">
            <div className="max-w-3xl mx-auto">
              <div className="inline-flex items-center px-3 py-1 bg-background-50 rounded-full mb-6">
                <span className="text-xs font-semibold text-accent-700 font-label">Beginner Guide</span>
              </div>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 font-heading leading-[1.1] mb-6">
                What Is A Sugargoo Spreadsheet? Complete Guide 2026
              </h1>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-200 flex items-center justify-center">
                    <span className="text-sm font-bold text-accent-700">ET</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">Editorial Team</p>
                    <p className="text-xs text-foreground-500">Updated January 2026</p>
                  </div>
                </div>
                <span className="text-xs text-foreground-400 px-3 py-1 bg-background-50 rounded-full">Editorial Standards</span>
                <span className="text-xs text-foreground-400">8 min read</span>
              </div>
            </div>
          </header>

          <div className="py-12 md:py-16 px-4 md:px-8 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <div className="prose-custom space-y-8">
                <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
                  If you have spent any time exploring the world of Chinese shopping agents, you have probably heard the term &quot;Sugargoo Spreadsheet&quot; thrown around in forums, Discord servers, and Reddit threads. But what exactly is it? At{' '}
                  <Link to="/" className="text-primary-500 hover:text-primary-600 underline font-medium cursor-pointer">SugargooSpreadsheet.run</Link>
                  , we have built the most comprehensive resource to help you understand, find, and use these spreadsheets effectively. This guide covers everything from the basics to advanced tips that will transform the way you shop through Sugargoo.
                </p>

                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mt-12 mb-4">
                  What Is A Sugargoo Spreadsheet?
                </h2>
                <p className="text-base text-foreground-700 leading-relaxed">
                  A Sugargoo Spreadsheet is a curated list of product links, seller names, prices, and categories designed to help buyers navigate the vast world of Chinese e-commerce platforms. Typically maintained as a Google Sheet or Notion page by dedicated community members, each spreadsheet row contains a product name, a direct link to the item on a Chinese marketplace such as Taobao or Weidian, the price in CNY, and often QC photos, seller ratings, or size recommendations.
                </p>
                <p className="text-base text-foreground-700 leading-relaxed">
                  Think of it as your personal shopping assistant. Instead of spending hours searching through hundreds of listings in a language you may not understand, a well-maintained Sugargoo Spreadsheet does the heavy lifting for you. It acts as the discovery layer — you browse, find what you like, and then paste the link directly into your Sugargoo dashboard to complete the purchase.
                </p>

                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mt-12 mb-4">
                  How Does A Sugargoo Spreadsheet Work?
                </h2>
                <p className="text-base text-foreground-700 leading-relaxed">
                  The workflow is remarkably straightforward. First, you open the spreadsheet and browse through the categories that interest you — whether that is sneakers, hoodies, accessories, or designer bags. Each entry includes a product name, an image preview, and a direct link. When you find something you like, you copy the product URL and paste it into your Sugargoo agent dashboard.
                </p>
                <p className="text-base text-foreground-700 leading-relaxed">
                  From that point, Sugargoo takes over as the fulfillment layer. Their team handles the domestic purchase from the Chinese seller, stores the item in their warehouse, takes quality check photos upon request, and arranges international shipping to your doorstep. The spreadsheet is purely the discovery and organization tool — Sugargoo is the engine that makes the transaction happen.
                </p>
                <p className="text-base text-foreground-700 leading-relaxed">
                  Many experienced users maintain their own private spreadsheets to track orders, compare prices across sellers, and save favorite finds for future reference. Community-shared spreadsheets, like the ones we feature on{' '}
                  <Link to="/" className="text-primary-500 hover:text-primary-600 underline font-medium cursor-pointer">SugargooSpreadsheet.run</Link>
                  , aggregate the best discoveries from multiple contributors and keep links updated as marketplace listings change.
                </p>

                <div className="my-12 overflow-x-auto">
                  <h3 className="text-xl font-bold text-foreground-950 font-heading mb-5">Feature Comparison: Sugargoo vs Alternative Agents</h3>
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="border-b-2 border-background-200">
                        <th className="text-left py-3 px-4 text-foreground-900 font-semibold">Feature</th>
                        <th className="text-left py-3 px-4 text-foreground-900 font-semibold">Sugargoo</th>
                        <th className="text-left py-3 px-4 text-foreground-900 font-semibold">Alternative Agents</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-background-200">
                        <td className="py-3 px-4 text-foreground-700">Ease of Use</td>
                        <td className="py-3 px-4 text-accent-700 font-medium">High</td>
                        <td className="py-3 px-4 text-foreground-500">Medium</td>
                      </tr>
                      <tr className="border-b border-background-200">
                        <td className="py-3 px-4 text-foreground-700">QC Photo Support</td>
                        <td className="py-3 px-4 text-accent-700 font-medium">Excellent</td>
                        <td className="py-3 px-4 text-foreground-500">Good</td>
                      </tr>
                      <tr className="border-b border-background-200">
                        <td className="py-3 px-4 text-foreground-700">Shipping Options</td>
                        <td className="py-3 px-4 text-accent-700 font-medium">Multiple</td>
                        <td className="py-3 px-4 text-foreground-500">Limited</td>
                      </tr>
                      <tr className="border-b border-background-200">
                        <td className="py-3 px-4 text-foreground-700">Spreadsheet Integration</td>
                        <td className="py-3 px-4 text-accent-700 font-medium">Native</td>
                        <td className="py-3 px-4 text-foreground-500">Manual</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-foreground-700">Community Trust</td>
                        <td className="py-3 px-4 text-accent-700 font-medium">Strong</td>
                        <td className="py-3 px-4 text-foreground-500">Varies</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mt-12 mb-4">
                  Top Categories to Explore in a Sugargoo Spreadsheet
                </h2>
                <p className="text-base text-foreground-700 leading-relaxed">
                  Our directory currently tracks 14 core categories, with inventory refreshed regularly. Sneakers and streetwear remain the most popular segments, but emerging categories like Perfumes and Designer Belts are growing rapidly. When using any spreadsheet, it is essential to check the &quot;Last Updated&quot; date. Dead links are common because marketplace listings on platforms like Taobao and Weidian expire frequently — sometimes within days or weeks.
                </p>
                <p className="text-base text-foreground-700 leading-relaxed">
                  Below is a quick reference for the most active categories. For the full directory with live links, return to our{' '}
                  <Link to="/" className="text-primary-500 hover:text-primary-600 underline font-medium cursor-pointer">homepage</Link> and browse the grid.
                </p>

                <div className="my-12 overflow-x-auto">
                  <h3 className="text-xl font-bold text-foreground-950 font-heading mb-5">Category Quick Reference</h3>
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="border-b-2 border-background-200">
                        <th className="text-left py-3 px-4 text-foreground-900 font-semibold">Category</th>
                        <th className="text-left py-3 px-4 text-foreground-900 font-semibold">Best For</th>
                        <th className="text-left py-3 px-4 text-foreground-900 font-semibold">Link Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-background-200">
                        <td className="py-3 px-4 text-foreground-700 font-medium">Shoes</td>
                        <td className="py-3 px-4 text-foreground-500">Sneakers &amp; Boots</td>
                        <td className="py-3 px-4"><span className="text-xs px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full">Active</span></td>
                      </tr>
                      <tr className="border-b border-background-200">
                        <td className="py-3 px-4 text-foreground-700 font-medium">Hoodies/Sweaters</td>
                        <td className="py-3 px-4 text-foreground-500">Streetwear Tops</td>
                        <td className="py-3 px-4"><span className="text-xs px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full">Active</span></td>
                      </tr>
                      <tr className="border-b border-background-200">
                        <td className="py-3 px-4 text-foreground-700 font-medium">Accessories</td>
                        <td className="py-3 px-4 text-foreground-500">Watches &amp; Jewelry</td>
                        <td className="py-3 px-4"><span className="text-xs px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full">Active</span></td>
                      </tr>
                      <tr className="border-b border-background-200">
                        <td className="py-3 px-4 text-foreground-700 font-medium">Bags</td>
                        <td className="py-3 px-4 text-foreground-500">Backpacks &amp; Purses</td>
                        <td className="py-3 px-4"><span className="text-xs px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full">Active</span></td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-foreground-700 font-medium">Jersey</td>
                        <td className="py-3 px-4 text-foreground-500">Sports &amp; Teams</td>
                        <td className="py-3 px-4"><span className="text-xs px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full">Active</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mt-12 mb-4">
                  Safety Tips &amp; Frequently Asked Questions
                </h2>
                <p className="text-base text-foreground-700 leading-relaxed">
                  Safety should always be your top priority when shopping through any agent platform. Here are essential guidelines: never pay a seller directly outside of Sugargoo&apos;s official payment system, always request QC photos before approving international shipment, cross-reference seller names against community-maintained blacklists, and be aware that spreadsheet links are community-curated and can expire without notice.
                </p>

                <div className="my-12 space-y-6">
                  <div className="bg-background-100 rounded-2xl p-6 md:p-8">
                    <h4 className="text-base font-bold text-foreground-900 font-heading mb-2">
                      Q: Is every link in a Sugargoo Spreadsheet guaranteed to work?
                    </h4>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      No. Spreadsheet links are community-curated and can expire at any time. Chinese marketplace listings are frequently removed, renamed, or relisted by sellers. Our team at{' '}
                      <Link to="/" className="text-primary-500 hover:text-primary-600 underline font-medium cursor-pointer">SugargooSpreadsheet.run</Link> updates the directory weekly, but individual links may go dead between refreshes. Always cross-check before committing to a purchase.
                    </p>
                  </div>

                  <div className="bg-background-100 rounded-2xl p-6 md:p-8">
                    <h4 className="text-base font-bold text-foreground-900 font-heading mb-2">
                      Q: Do I need a Sugargoo account to use these spreadsheets?
                    </h4>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      Yes. An active Sugargoo account is required to paste product links, view domestic pricing, request QC photos, and arrange international shipping. The spreadsheet is purely a discovery tool — Sugargoo handles everything related to purchasing and logistics.
                    </p>
                  </div>

                  <div className="bg-background-100 rounded-2xl p-6 md:p-8">
                    <h4 className="text-base font-bold text-foreground-900 font-heading mb-2">
                      Q: What does QC mean and why is it important?
                    </h4>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      QC stands for Quality Control. Once Sugargoo receives your item at their warehouse, you can request QC photos — high-resolution images that show the actual product you will receive. These photos help you verify that the item matches the listing description, has no visible defects, and is the correct size or color before it ships internationally.
                    </p>
                  </div>

                  <div className="bg-background-100 rounded-2xl p-6 md:p-8">
                    <h4 className="text-base font-bold text-foreground-900 font-heading mb-2">
                      Q: Are the products in these spreadsheets authentic?
                    </h4>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      Most community Sugargoo Spreadsheets focus on replica, budget-friendly, or unbranded alternatives rather than authentic luxury goods. If authenticity is important to you, always read the item description carefully, check seller ratings, and review QC photos thoroughly. Some spreadsheets do include links to legitimate branded items, but these are less common.
                    </p>
                  </div>

                  <div className="bg-background-100 rounded-2xl p-6 md:p-8">
                    <h4 className="text-base font-bold text-foreground-900 font-heading mb-2">
                      Q: How do I report a broken link or suggest a new find?
                    </h4>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      Return to our{' '}
                      <Link to="/" className="text-primary-500 hover:text-primary-600 underline font-medium cursor-pointer">homepage</Link> and use the resources listed in the footer. We welcome community contributions and strive to keep our directory as accurate and up-to-date as possible.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-16 bg-accent-50 rounded-2xl p-8 md:p-10 text-center">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                  Ready to Shop?
                </h2>
                <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto leading-relaxed">
                  Visit our curated store for the latest verified Sugargoo spreadsheet finds across all 14 categories.
                </p>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-foreground-900 text-background-50 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
                >
                  VISIT KASPREADSHEET
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </a>
              </div>

              <div className="mt-12 pt-8 border-t border-background-200">
                <p className="text-xs text-foreground-400">
                  Return to{' '}
                  <Link to="/" className="text-primary-500 hover:text-primary-600 underline font-medium cursor-pointer">Homepage</Link>
                  {' '}| Read more{' '}
                  <Link to="/" className="text-primary-500 hover:text-primary-600 underline font-medium cursor-pointer">Guides</Link>
                  {' '}| Browse{' '}
                  <Link to="/" className="text-primary-500 hover:text-primary-600 underline font-medium cursor-pointer">All Categories</Link>
                </p>
                <p className="text-xs text-foreground-300 mt-4 leading-relaxed">
                  <strong className="text-foreground-400">Disclaimer:</strong> This website is an independent informational resource and is not affiliated with Sugargoo or any listed brands. Information may change over time and users should perform their own verification before making purchasing decisions.
                </p>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}